import axios from "axios";
import { API } from "./client";

export async function getProjects() {
  const res = await axios.get(`${API}/projects`);
  return res.data;
}

export async function getProject(id) {
  const res = await axios.get(`${API}/projects/${id}`);
  return res.data;
}

export async function getRoutingNotes(projectId) {
  const res = await axios.get(`${API}/projects/${projectId}/routing-notes`);
  return res.data;
}

export async function createRoutingNote(projectId, data) {
  const res = await axios.post(`${API}/projects/${projectId}/routing-notes`, data);
  return res.data;
}

export async function updateRoutingNote(projectId, noteId, data) {
  const res = await axios.patch(`${API}/projects/${projectId}/routing-notes/${noteId}`, data);
  return res.data;
}

export async function deleteRoutingNote(projectId, noteId, expectedRevision) {
  const res = await axios.delete(`${API}/projects/${projectId}/routing-notes/${noteId}`, {
    params: { expected_revision: expectedRevision },
  });
  return res.data;
}

export async function getRoutingNoteHistory(projectId, noteId) {
  const res = await axios.get(`${API}/projects/${projectId}/routing-notes/${noteId}/history`);
  return res.data;
}

export async function getFinancialsSummary() {
  const res = await axios.get(`${API}/financials/summary`);
  return res.data;
}

export async function setProjectHours(id, hours) {
  const res = await axios.patch(`${API}/projects/${id}/hours`, { hours });
  return res.data;
}

export async function reEstimateHours(id) {
  const res = await axios.post(`${API}/projects/${id}/estimate-hours`);
  return res.data;
}

export async function retryXeroSync(id) {
  const res = await axios.post(`${API}/projects/${id}/xero/retry-sync`);
  return res.data;
}

export async function createProject(data) {
  const res = await axios.post(`${API}/projects`, data);
  return res.data;
}

export async function updateProject(id, data) {
  const res = await axios.put(`${API}/projects/${id}`, data);
  return res.data;
}

export async function deleteProject(id, deleteOneDrive = false) {
  const res = await axios.delete(`${API}/projects/${id}`, {
    params: { delete_onedrive: deleteOneDrive },
  });
  return res.data;
}

export async function getProjectEmailLogs(id, { search, pageSize = 20, cursor = 0 } = {}) {
  const params = { page_size: pageSize, cursor };
  if (search) params.search = search;
  const res = await axios.get(`${API}/projects/${id}/email-logs`, { params });
  return res.data;
}

export async function getEmailBody(projectId, intakeId) {
  const res = await axios.get(`${API}/projects/${projectId}/emails/${intakeId}/body`);
  return res.data;
}

export async function triggerBackfill(id) {
  const res = await axios.post(`${API}/projects/${id}/backfill-inbox`);
  return res.data;
}

export async function getBackfillStatus(id) {
  const res = await axios.get(`${API}/projects/${id}/backfill-status`);
  return res.data;
}

export async function getTrainingLog(id) {
  const res = await axios.get(`${API}/projects/${id}/training-log`);
  return res.data;
}

export async function removeTrainingEntry(projectId, entryId) {
  const res = await axios.delete(`${API}/projects/${projectId}/training-log/${entryId}`);
  return res.data;
}

export async function enableContractor(projectId, contractorName, contactName = null, contactEmail = null) {
  const body = { contractor_name: contractorName };
  if (contactName) body.contact_name = contactName;
  if (contactEmail) body.contact_email = contactEmail;
  const res = await axios.post(`${API}/projects/${projectId}/contractors/enable`, body);
  return res.data;
}

export async function importFromOneDrive(projectId, sourceFolderIds) {
  const res = await axios.post(`${API}/projects/${projectId}/import-onedrive`, {
    source_folder_ids: sourceFolderIds,
  });
  return res.data;
}

export async function getImportStatus(projectId) {
  const res = await axios.get(`${API}/projects/${projectId}/import-status`);
  return res.data;
}


export async function getRoutingTraces(projectId, limit = 20) {
  const res = await axios.get(`${API}/projects/${projectId}/routing-traces`, { params: { limit } });
  return res.data;
}

export async function getRoutingAccuracyStats(days = 30) {
  const res = await axios.get(`${API}/email-routing/accuracy`, { params: { days } });
  return res.data;
}

export async function replayIntake(intakeId) {
  const res = await axios.post(`${API}/email-routing/${intakeId}/replay`);
  return res.data;
}

// ── Sub-jobs (F-019) ──────────────────────────────────────────────────────────

export async function getSubJobs(projectId) {
  const res = await axios.get(`${API}/projects/${projectId}/sub-jobs`);
  return res.data;
}

export async function getProjectFinancialSummary(projectId) {
  const res = await axios.get(`${API}/projects/${projectId}/financial-summary`);
  return res.data;
}

export async function createSubJob(projectId, data) {
  const res = await axios.post(`${API}/projects/${projectId}/sub-jobs`, data);
  return res.data;
}

export async function updateSubJob(projectId, subJobId, data) {
  const res = await axios.put(`${API}/projects/${projectId}/sub-jobs/${subJobId}`, data);
  return res.data;
}

export async function archiveSubJob(projectId, subJobId) {
  const res = await axios.delete(`${API}/projects/${projectId}/sub-jobs/${subJobId}`);
  return res.data;
}

// ── Monthly sub-job folders (F-020) ───────────────────────────────────────────

export async function createSubJobMonthFolder(projectId, subJobId, year, month) {
  const res = await axios.post(
    `${API}/projects/${projectId}/sub-jobs/${subJobId}/create-month-folder`,
    null,
    { params: { year, month } }
  );
  return res.data;
}

// ── PO Request & Invoice (F-022 / F-024) ─────────────────────────────────────

export async function requestPo(projectId, mode = "send", subJobId = null) {
  const body = { mode };
  if (subJobId) body.sub_job_id = subJobId;
  const res = await axios.post(`${API}/projects/${projectId}/request-po`, body);
  return res.data;
}

export async function sendInvoice(projectId, subJobId = null) {
  const params = subJobId ? { sub_job_id: subJobId } : {};
  const res = await axios.post(`${API}/projects/${projectId}/send-invoice`, null, { params });
  return res.data;
}

// ── Xero Quotes (F-025) ──────────────────────────────────────────────────────

export async function generateQuote(projectId, subJobId = null) {
  const params = subJobId ? { sub_job_id: subJobId } : {};
  const res = await axios.post(`${API}/projects/${projectId}/quote/generate`, null, { params });
  return res.data;
}

export async function sendQuote(projectId, subJobId = null) {
  const params = subJobId ? { sub_job_id: subJobId } : {};
  const res = await axios.post(`${API}/projects/${projectId}/quote/send`, null, { params });
  return res.data;
}

export async function checkQuoteStatus(projectId, subJobId = null) {
  const params = subJobId ? { sub_job_id: subJobId } : {};
  const res = await axios.post(`${API}/projects/${projectId}/quote/check-status`, null, { params });
  return res.data;
}


// ── PO Lifecycle (F-022) ──────────────────────────────────────────────────────

export async function confirmPo(projectId, poNumber, subJobId = null) {
  const params = { po_number: poNumber };
  if (subJobId) params.sub_job_id = subJobId;
  const res = await axios.post(`${API}/projects/${projectId}/confirm-po`, null, { params });
  return res.data;
}
